// Lista de magias como no cliente: ícone, palavras, level, mana e detalhes; arraste para a barra de ações.
// Visual de livro de magias: capa arcana, páginas escuras, runas brilhando. CSS em client/css-parts/cyclo.css (prefixo .sl-).
import { BUFFS } from '../../../../shared/src/game/buffs';
import { useEffect, useState } from 'preact/hooks';
import { AREA_NAMES, ELEMENT_NAMES, GROUP_NAMES, spellBlock, type SpellDef } from '../../../../shared/src/game/spells';
import { getVocation, type VocationId } from '../../../../shared/src/game/vocations';
import { loadSpells } from '../spells';
import { CHALLENGE_SPELLS, CONDITION_CURES, DEBUFF_SPELLS, FAMILIARS, HEAL_SPELLS, PARTY_BUFFS } from '../../../../shared/src/game/helper';
const usable = (s: SpellDef) => s.usable || CHALLENGE_SPELLS.includes(s.words) || !!PARTY_BUFFS[s.words] || !!FAMILIARS[s.words] || !!DEBUFF_SPELLS[s.words] || !!BUFFS[s.words] || (s.kind === 'instant' && (HEAL_SPELLS.some((h) => h.words === s.words) || CONDITION_CURES.some((c) => c.spells.includes(s.words))));
import { SpellIcon } from './SpellIcon';
import { conjureByWords } from '../../../../shared/src/game/conjure';
import { ItemImg } from './ItemSlot';

type Filter = 'all' | 'attack' | 'healing' | 'support' | 'rune';
const FILTER_ICON: Record<Filter, string> = { all: '📚', attack: '⚔️', healing: '💚', support: '🛡️', rune: '🔷' };
const secs = (ms: number) => (ms / 1000).toFixed(0) + ' s';

export function SpellList(props: { vocation: VocationId; level: number; ml: number; promotion?: number; onAssign: (s: SpellDef) => void; onConjure?: (words: string) => void }) {
  const [list, setList] = useState<SpellDef[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [sel, setSel] = useState<SpellDef | null>(null);
  const [onlyMine, setOnlyMine] = useState(true);
  const [help, setHelp] = useState(false);
  useEffect(() => { loadSpells().then(setList); }, []);
  const shown = list.filter((s) => (!onlyMine || s.vocations.includes(props.vocation))
    && (filter === 'all' || (filter === 'rune' ? s.kind === 'rune' : s.group === filter && s.kind !== 'rune'))
    && (s.kind === 'rune' || s.words));
  const blockOf = (s: SpellDef) => spellBlock(s, props.vocation, props.level, props.ml, props.promotion ?? 0);
  // progresso do livro: magias e runas da vocation liberadas e a próxima a liberar por level
  const mine = list.filter((s) => s.vocations.includes(props.vocation) && (s.kind === 'rune' || s.words));
  const open = mine.filter((s) => !blockOf(s)).length;
  const next = mine.filter((s) => s.level > props.level).sort((a, b) => a.level - b.level)[0];
  const vocName = getVocation(props.vocation)?.name ?? props.vocation;
  const desc = (s: SpellDef) => {
    const parts: string[] = [];
    parts.push(`${s.kind === 'rune' ? 'Runa' : 'Magia'} de ${(GROUP_NAMES[s.group] ?? s.group).toLowerCase()}${s.element && s.element !== 'heal' ? ` (${ELEMENT_NAMES[s.element] ?? s.element})` : ''}.`);
    if (s.group === 'attack' || s.kind === 'rune') parts.push(`${AREA_NAMES[s.area.type]}${s.area.radius && s.area.type !== 'single' ? `, raio ${s.area.radius}` : ''}${s.area.range > 1 ? `, alcance ${s.area.range}` : ''}.`);
    if (BUFFS[s.words]) parts.push(BUFFS[s.words]!.desc + ' Na barra de ações, a caça automática reaplica quando acabar.');
    if (s.areaText) parts.push(`Área (TibiaWiki): ${s.areaText}.`);
    return parts.join(' ');
  };
  return (
    <div class="sl">
      <div class="sl-head">
        <div class="sl-tome" aria-hidden="true"><span>📕</span><i class="sl-rune r1">ᚱ</i><i class="sl-rune r2">ᛟ</i><i class="sl-rune r3">ᚦ</i></div>
        <div class="sl-head-txt">
          <div class="sl-title">Livro de Magias
            <button class={'sl-help' + (help ? ' on' : '')} onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="sl-sub">Magias e runas de <b>{vocName}</b>. Arraste para a barra de ações e a caça automática usa sozinha.</div>
          {mine.length > 0 && <div class="sl-meter" title={`${open} de ${mine.length} liberadas`}>
            <i style={{ width: (open / mine.length) * 100 + '%' }} /><span>{open} de {mine.length} liberadas</span></div>}
        </div>
        <div class="sl-head-stat" title="Próxima magia ou runa da sua vocation liberada por level">
          <small>Próxima</small>
          {next ? <><b>Lv {next.level}</b><span>{next.name}</span></> : <><b>✓</b><span>todas por level</span></>}
        </div>
      </div>

      {help && (
        <div class="sl-steps">
          <div><i>📖</i><b>1. Escolha</b><small>Clique numa magia para ver mana, cooldown, área e o que ela faz.</small></div>
          <div><i>🖐️</i><b>2. Arraste</b><small>Arraste da lista para um espaço da barra de ações (ou use o botão no detalhe).</small></div>
          <div><i>⚔️</i><b>3. Cace</b><small>Na caça automática, as magias da barra são usadas sozinhas; buffs são reaplicados.</small></div>
          <div><i>🔒</i><b>4. Libere</b><small>As esmaecidas mostram o que falta: level, magic level ou promotion.</small></div>
        </div>
      )}

      <div class="sl-bar">
        <div class="sl-filters">
          {(['all', 'attack', 'healing', 'support', 'rune'] as Filter[]).map((f) => (
            <button key={f} class={'sl-filter' + (filter === f ? ' on' : '')} onClick={() => setFilter(f)}>
              <i>{FILTER_ICON[f]}</i>{f === 'all' ? 'Todas' : f === 'rune' ? 'Runas' : GROUP_NAMES[f]}</button>
          ))}
        </div>
        <label class="sl-mine"><input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine((e.target as HTMLInputElement).checked)} /> Só da minha vocation</label>
      </div>

      <div class="sl-body">
        <ul class="sl-list">
          {shown.map((s) => {
            const block = blockOf(s);
            const soon = !usable(s) && !conjureByWords(s.words);
            return (
              <li key={s.name} class={'sl-row g-' + (s.kind === 'rune' ? 'rune' : s.group) + (sel === s ? ' sel' : '') + (block ? ' locked' : '')} onClick={() => setSel(s)}
                draggable={!block && usable(s)} onDragStart={(e) => { e.dataTransfer!.setData('application/x-noxara-spell', s.kind === 'rune' ? 'i:' + s.item : 's:' + s.words); }}>
                <span class="sl-ico">{s.kind === 'rune' && s.item ? <span class="item-slot small"><ItemImg typeId={s.item} count={1} /></span> : <SpellIcon spell={s} size={32} dim={!!block} />}</span>
                <span class="sl-name"><b>{s.name}</b><small>{s.kind === 'rune' ? `ML ${s.ml}` : s.words}</small></span>
                <span class="sl-tags">
                  {s.kind !== 'rune' && <em class="mana" title="Mana">{s.mana}</em>}
                  <em class="cd" title="Cooldown">{secs(s.cooldown)}</em>
                </span>
                {block ? <span class="sl-lock" title={`Bloqueada: ${block}`}>🔒 {s.level > props.level && s.vocations.includes(props.vocation) ? `Lv ${s.level}` : block}</span>
                  : soon ? <span class="sl-soon">em breve</span> : <span class="sl-lv">Lv {s.level}</span>}
              </li>
            );
          })}
          {!shown.length && <li class="sl-empty muted">{list.length ? 'Nenhuma magia neste filtro.' : 'Carregando...'}</li>}
        </ul>
        <div class="sl-detail">
          {sel ? (() => {
            const b = blockOf(sel);
            const cj = conjureByWords(sel.words);
            return (
              <>
                <div class={'sl-hero g-' + (sel.kind === 'rune' ? 'rune' : sel.group) + (b ? ' locked' : '')}>
                  <span class="sl-big">{sel.kind === 'rune' && sel.item ? <span class="item-slot"><ItemImg typeId={sel.item} count={1} /></span> : <SpellIcon spell={sel} size={48} dim={!!b} />}</span>
                  <div class="sl-hero-txt">
                    <h4>{sel.name}</h4>
                    {sel.words && <span class="sl-words">"{sel.words}"</span>}
                    <small>{sel.kind === 'rune' ? 'Runa' : 'Instantânea'} · {GROUP_NAMES[sel.group]}</small>
                  </div>
                </div>
                <div class="sl-stats">
                  <div class={props.level >= sel.level ? 'ok' : 'no'}><small>Level</small><b>{sel.level}</b></div>
                  {sel.kind === 'rune' ? <div class={props.ml >= sel.ml ? 'ok' : 'no'}><small>Magic level</small><b>{sel.ml}</b></div>
                    : <div class="mana"><small>Mana</small><b>{sel.mana}</b></div>}
                  <div><small>Cooldown</small><b>{secs(sel.cooldown)}</b><span>grupo {secs(sel.groupCooldown)}</span></div>
                </div>
                <table class="kv-table sl-kv"><tbody>
                  <tr><td>Vocations</td><td>{sel.vocations.join(', ')}</td></tr>
                  <tr><td>Alvo</td><td>{AREA_NAMES[sel.area.type]}</td></tr>
                  {sel.kind === 'rune' && sel.price ? <tr><td>Preço no NPC</td><td>{sel.price} gp</td></tr> : null}
                </tbody></table>
                <p class="sl-desc">{desc(sel)}</p>
                {b ? <p class="sl-block">🔒 Você não pode usar: <b>{b}</b>.</p> : null}
                {cj && (() => {
                  const why = !cj.vocations.includes(props.vocation) ? 'não é da sua vocation' : props.level < cj.level ? `precisa de level ${cj.level}` : '';
                  return <div class="sl-conj"><p>✨ Conjurar ({sel.words}): <b>{cj.count}x {cj.name}</b> por <b>{cj.mana}</b> de mana, direto na Supply Pouch (sem blank rune). Treina o magic level.</p>
                    <div><button class="tb-button green" disabled={!!why} title={why} onClick={() => props.onConjure?.(sel.words)}>Conjurar</button>{why && <small class="muted"> ({why})</small>}</div></div>;
                })()}
                {cj && sel.kind !== 'rune' ? null : usable(sel) ? <button class="tb-button green sl-assign" disabled={!!b} onClick={() => props.onAssign(sel)}>Colocar na barra de ações</button>
                  : <p class="muted small">Esta magia ainda não funciona na caça automática.</p>}
                <p class="muted small">Dica: arraste da lista para um espaço da barra de ações.</p>
              </>
            );
          })() : <div class="sl-none"><span>📜</span><p class="muted">Clique numa magia para ver os detalhes.</p></div>}
        </div>
      </div>
    </div>
  );
}
